import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Mail, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = ({ type = 'employer' }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const emailTo = type === 'jobseeker' 
    ? 'resumeworknation@gmail.com' 
    : 'worknationlabourltd@gmail.com';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        type,
        emailTo,
      });

      // Fire Google Ads conversion tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17887171981/DSNyCM_q2ugbEI2rotFC'
        });
      }

      if (response.data.email_sent === false) {
        toast({
          title: 'Message Saved',
          description: 'Your message was received but email delivery failed. We will follow up shortly.',
        });
      } else {
        toast({
          title: 'Message Sent!',
          description: 'Thank you for contacting us. We\'ll get back to you soon.',
        });
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try emailing us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 border-t-4 border-[#003366]">
      <div className="flex items-center mb-6">
        <Mail className="text-[#003366] mr-3" size={32} />
        <div>
          <h3 className="text-2xl font-bold text-[#003366]">
            {type === 'jobseeker' ? 'Contact Us - Job Seekers' : 'Contact Us - Employers'}
          </h3>
          <p className="text-gray-600 text-sm mt-1">
            Email: <a href={`mailto:${emailTo}`} className="text-[#003366] hover:underline">{emailTo}</a>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-gray-700 font-semibold">
            Name *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name"
            className="border-gray-300 focus:border-[#003366] focus:ring-[#003366]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-gray-700 font-semibold">
            Email *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            className="border-gray-300 focus:border-[#003366] focus:ring-[#003366]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-gray-700 font-semibold">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="(604) 123-4567"
            className="border-gray-300 focus:border-[#003366] focus:ring-[#003366]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="message" className="text-gray-700 font-semibold">
            Questions / Comments *
          </Label>
          <Textarea
            id="message"
            name="message"
            required
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us how we can help you..."
            rows={6}
            className="border-gray-300 focus:border-[#003366] focus:ring-[#003366] resize-none"
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#003366] hover:bg-[#004488] text-white py-6 text-lg font-semibold transition-all duration-300"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={20} />
              Sending...
            </>
          ) : (
            <>
              <Mail className="mr-2" size={20} />
              Send Message
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;